import React, { useState, useEffect, useContext } from 'react';
import AppContext from '../../../contexts/AppContext';
import RolesContext from '../../../contexts/RolesContext';
import ToggleSmall from '../../Shared/Toggle/ToggleSmall';
import tenantApi from '../../../utils/api/tenant';
import { toast } from 'react-toastify';
import { useTenantFeature } from '../../../hooks/features';
import { Tooltip, Select, MenuItem, FormControl } from '@mui/material';
import { useLogo } from '../../../contexts/AppLogoContext';
import rolesApi from '../../../utils/api/roles';
import { FormContext, SchemaFieldsWithContextUI } from '@sw-sw/lib-form';
import { UIControlType } from '@sw-sw/lib-form-control-types';
import { ActionButtons } from '../../Shared/ActionButtons';
import { submitHandler } from '../../Shared/form';
import { useHistory } from 'react-router-dom';
import { Modal } from '@sw-sw/lib-ui';
import { customDashboard } from '../../../utils/api/dashboard';
import DivisionContext from '../../../contexts/DivisionContext';
interface FeaturesFormat {
  feature: {
    feature_type: string;
    name: string;
    tooltip: string;
  };
  id: any;
  is_enabled: boolean;
  max_inspection_templates?: number;
}

const Feature = (
  title: string,
  featuresList: FeaturesFormat[],
  handleToggle: (
    id: number,
    currentInclude: boolean,
    feature_type?: string,
  ) => void,
) => {
  const [collapse, setCollapse] = useState(false);
  const permCheck = useContext(RolesContext).userHasPermission;

  return (
    <div className='settings-tenant-main-container'>
      <div
        className='settings-main-tenant-text'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '30px',
          cursor: 'pointer',
        }}
        onClick={() => setCollapse(!collapse)}
      >
        <h3>{title}</h3>
        <i
          className={collapse ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
          aria-hidden='true'
        />
      </div>
      <hr></hr>
      <div
        className={
          collapse ? 'administration-hidden' : 'administration-visible'
        }
        style={{ marginBottom: '20px' }}
      >
        {featuresList.map((ele) => {
          return ele.feature.tooltip ? (
            <div className='features-table-body' key={ele.id}>
              <Tooltip
                title={ele.feature.tooltip}
                placement='top'
                arrow
                enterTouchDelay={0}
              >
                <h3>{ele.feature.name}</h3>
              </Tooltip>
              <h3 className='features-table-body-toggle'>
                <ToggleSmall
                  isChecked={ele.is_enabled}
                  handleChange={() => handleToggle(ele.id, ele.is_enabled)}
                  disabled={!permCheck('all', 'Administration Features')}
                />
              </h3>
            </div>
          ) : (
            <div className='features-table-body' key={ele.id}>
              <h3>{ele.feature.name}</h3>
              <h3 className='features-table-body-toggle'>
                <ToggleSmall
                  isChecked={ele.is_enabled}
                  handleChange={() =>
                    handleToggle(
                      ele.id,
                      ele.is_enabled,
                      ele.feature.feature_type,
                    )
                  }
                  disabled={!permCheck('all', 'Administration Features')}
                />
              </h3>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const FeatureWithAction = (
  title: string,
  featuresList: FeaturesFormat[],
  handleAction: (id: number, feature_type?: string) => void,
) => {
  const [collapse, setCollapse] = useState(false);

  return (
    <div className='settings-tenant-main-container'>
      <div
        className='settings-main-tenant-text'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '30px',
          cursor: 'pointer',
        }}
        onClick={() => setCollapse(!collapse)}
      >
        <h3>{title}</h3>
        <i
          className={collapse ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
          aria-hidden='true'
        />
      </div>
      <hr></hr>
      <div
        className={
          collapse ? 'administration-hidden' : 'administration-visible'
        }
        style={{ marginBottom: '20px' }}
      >
        {featuresList.map((ele) => {
          return ele.feature.tooltip ? (
            <div className='features-table-body' key={ele.id}>
              <Tooltip
                title={ele.feature.tooltip}
                placement='top'
                arrow
                enterTouchDelay={0}
              >
                <h3>{ele.feature.name}</h3>
              </Tooltip>
              <div className='features-table-body-toggle'>
                <ActionButtons
                  primary='Run'
                  onClick={() => handleAction(ele.id, ele.feature.feature_type)}
                />
              </div>
            </div>
          ) : (
            <div className='features-table-body' key={ele.id}>
              <h3>{ele.feature.name}</h3>
              <div className='features-table-body-button'>
                <ActionButtons
                  primary='Run'
                  onClick={() => handleAction(ele.id, ele.feature.feature_type)}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const MultipleFormsFeature = (
  title: string,
  featuresList: FeaturesFormat[],
  handleToggle: (
    id: number,
    currentInclude: boolean,
    feature_type?: string,
  ) => void,
  tenantId: number,
) => {
  const [collapse, setCollapse] = useState(false);
  const permCheck = useContext(RolesContext).userHasPermission;

  // Find the multiple forms feature
  const multipleFormsFeature = featuresList.find(
    (ele) => ele.feature.name === 'Enable multiple forms per project'
  );

  const [maxFormsCount, setMaxFormsCount] = useState(1);

  // Update maxFormsCount when multipleFormsFeature data is loaded
  useEffect(() => {
    if (multipleFormsFeature?.max_inspection_templates) {
      setMaxFormsCount(multipleFormsFeature.max_inspection_templates);
    }
  }, [multipleFormsFeature?.max_inspection_templates]);

  const handleMaxFormsChange = async (event: any) => {
    const newValue = event.target.value;
    setMaxFormsCount(newValue);
    
    try {
      await tenantApi.updateMaxInspectionTemplates(tenantId, newValue);
      toast.success(`Maximum forms per project set to ${newValue}`);
    } catch (error) {
      toast.error('Error updating maximum forms per project');
      console.error('Error updating max inspection templates:', error);
    }
  };

  return (
    <div className='settings-tenant-main-container'>
      <div
        className='settings-main-tenant-text'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '30px',
          cursor: 'pointer',
        }}
        onClick={() => setCollapse(!collapse)}
      >
        <h3>{title}</h3>
        <i
          className={collapse ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
          aria-hidden='true'
        />
      </div>
      <hr></hr>
      <div
        className={
          collapse ? 'administration-hidden' : 'administration-visible'
        }
        style={{ marginBottom: '20px' }}
      >
        {/* Regular features */}
        {featuresList
          .filter((ele) => ele.feature.name !== 'Enable multiple forms per project')
          .map((ele) => {
            return ele.feature.tooltip ? (
              <div className='features-table-body' key={ele.id}>
                <Tooltip
                  title={ele.feature.tooltip}
                  placement='top'
                  arrow
                  enterTouchDelay={0}
                >
                  <h3>{ele.feature.name}</h3>
                </Tooltip>
                <h3 className='features-table-body-toggle'>
                  <ToggleSmall
                    isChecked={ele.is_enabled}
                    handleChange={() => handleToggle(ele.id, ele.is_enabled)}
                    disabled={!permCheck('all', 'Administration Features')}
                  />
                </h3>
              </div>
            ) : (
              <div className='features-table-body' key={ele.id}>
                <h3>{ele.feature.name}</h3>
                <h3 className='features-table-body-toggle'>
                  <ToggleSmall
                    isChecked={ele.is_enabled}
                    handleChange={() =>
                      handleToggle(
                        ele.id,
                        ele.is_enabled,
                        ele.feature.feature_type,
                      )
                    }
                    disabled={!permCheck('all', 'Administration Features')}
                  />
                </h3>
              </div>
            );
          })}
        
        {/* Custom Multiple Forms Feature */}
        {multipleFormsFeature && (
          <div className='features-table-body' key={multipleFormsFeature.id}>
            <div style={{ display: 'flex', alignItems: 'center', width: '100%' }}>
              <h3 style={{ margin: 0, flex: 1 }}>Enable multiple forms per project</h3>
              <FormControl size="small" style={{ minWidth: 80 }}>
                <Select
                  value={maxFormsCount}
                  onChange={handleMaxFormsChange}
                  disabled={!multipleFormsFeature.is_enabled || !permCheck('all', 'Administration Features')}
                  style={{ 
                    height: '32px',
                    fontSize: '14px'
                  }}
                >
                  {[1, 2, 3, 4, 5].map((num) => (
                    <MenuItem key={num} value={num}>
                      {num}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <div style={{ marginLeft: 'auto', paddingLeft: '20px' }}>
                <ToggleSmall
                  isChecked={multipleFormsFeature.is_enabled}
                  handleChange={() =>
                    handleToggle(
                      multipleFormsFeature.id,
                      multipleFormsFeature.is_enabled,
                      multipleFormsFeature.feature.feature_type,
                    )
                  }
                  disabled={!permCheck('all', 'Administration Features')}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

const Roles = () => {
  const formContext = useContext(FormContext);
  const [rolesWithStatus, setRolesWithStatus] = useState<any[]>([]);
  const [collapse, setCollapse] = useState(false);
  const history = useHistory();
  

  useEffect(() => {
    let isMounted = true;

    rolesApi.getRolesWithStatus().then((res) => {
      if (isMounted) {
        setRolesWithStatus(res);
      }
    });

    return () => {
      isMounted = false;
    };
  }, []);

  const handleSubmit = async (formData: any) => {
    const updatedRoles = rolesWithStatus.map((ele) => {
      const isEnabled = formData.roles.some((role: any) => role.id === ele.id);
      
      return {
        id: ele.tenantRoles[0].id,
        role_id: ele.id,
        name: ele.name,
        is_enabled: isEnabled,
      };
    });

    rolesApi.updateTenantRoles({ rolesWithStatus: updatedRoles }).then(() => {
      toast.success('Roles updated successfully');
      history.go(0);
    });
  };

  if (rolesWithStatus.length === 0) {
    return (
      <div
        className='settings-tenant-main-container'
        style={{ marginBottom: '5rem' }}
      >
        <div
          className='settings-main-tenant-text'
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '30px',
          }}
        >
          <h3>Roles</h3>
        </div>
        <hr></hr>
        Loading...
      </div>
    );
  }

  return (
    <div
      className='settings-tenant-main-container'
      style={{ marginBottom: '5rem' }}
    >
      <div
        className='settings-main-tenant-text'
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          marginTop: '30px',
        }}
        onClick={() => setCollapse(!collapse)}
      >
        <h3>Roles</h3>
        <i
          className={collapse ? 'fa fa-chevron-down' : 'fa fa-chevron-up'}
          aria-hidden='true'
        />
      </div>
      <hr></hr>
      <div
        className={
          collapse ? 'administration-hidden' : 'administration-visible'
        }
      >
        <SchemaFieldsWithContextUI
          schema={{
            roles: {
              label: 'Roles',
              controlType: UIControlType.customTagSelect,
              isMulti: true,
              openOnFocus: true,
              showSelectAll: true,
              labelKey: 'name',
              valueKey: 'id',
              options: rolesWithStatus,
              showClearAll: true,
              style: {
                width: '100%',
                height: 'auto',
                marginTop: '10px',
                marginRight: '0.5rem',
                marginLeft: '0.5rem',
              },
            },
          }}
          initialFormData={{
            roles: rolesWithStatus.filter(
              (ele) => ele.tenantRoles[0].is_enabled,
            ),
          }}
        />
        <ActionButtons
          primary='Save'
          onClick={(e: any) => submitHandler(formContext, e, handleSubmit)}
        />
      </div>
    </div>
  );
};

export const AllFeatures = (props: any) => {
  const [featuresList, setfeaturesList] = useState<FeaturesFormat[]>([]);
  const [showActionModal, setShowActionModal] = useState(false);
  const [selectedFeature, setSelectedFeature] = useState<FeaturesFormat | null>(null);
  const appContext = useContext(AppContext);
  const divisionContext = useContext(DivisionContext);
  const tenantFeatureQuery = useTenantFeature();
  const { refetchLogo } = useLogo();
  const user = appContext.get("user");

  const userTenantId =
    appContext &&
    appContext.state &&
    appContext.state.user &&
    appContext.state.user.tenantId;

  useEffect(() => {
    let isMounted = true;

    const loadInitialData = () => {
      tenantApi.features.index(userTenantId).then((res) => {
        if (isMounted) {
          setfeaturesList(res);
        }
      });
    };

    loadInitialData();

    return () => {
      isMounted = false;
    };
  }, []);

  const handleToggle = async (
    id: number,
    currentInclude: boolean,
    feature_type: string | null = null,
  ) => {
    const updatedFeaturesList = featuresList.map((ele) => {
      if (ele.id === id) {
        return {
          feature: {
            feature_type: ele.feature.feature_type,
            name: ele.feature.name,
            tooltip: ele.feature.tooltip,
          },
          id: id,
          is_enabled: !currentInclude,
        };
      }

      return ele;
    });

    setfeaturesList(updatedFeaturesList);
    tenantApi.features
      .update(userTenantId, id, !currentInclude)
      .then(() => {
        tenantFeatureQuery.refetch();
        if (feature_type === 'white_label') {
          refetchLogo(userTenantId);
          props.setIsWhiteLabel(!currentInclude);
        }
        toast.success(
          `Feature ${
            currentInclude === false ? 'Enabled' : 'Disabled'
          } Successfully!`,
        );
      })
      .catch(() => {
        toast.error('Error updating feature');

        const errorhandledFeaturesList = featuresList.map((ele) => {
          if (ele.id === id) {
            return {
              feature: {
                feature_type: ele.feature.feature_type,
                name: ele.feature.name,
                tooltip: ele.feature.tooltip,
              },
              id: id,
              is_enabled: currentInclude,
            };
          }

          return ele;
        });

        setfeaturesList(errorhandledFeaturesList);
      });
  };

  const handleAction = async (id: number, feature_type?: string) => {
    try {
      const feature = featuresList.find((f) => f.id === id);

      if (feature && feature_type === 'national_dashboard_script') {
        setSelectedFeature(feature);
        setShowActionModal(true);
      }
    } catch (error) {
      toast.error('Error opening feature details');
    }
  };

  const executeFeatureAction = async () => {
    try {
      if (selectedFeature) {
        if (selectedFeature.feature.feature_type === 'national_dashboard_script') {
          const divisionId = (divisionContext && divisionContext.selectedDivision) ? divisionContext.selectedDivision.id : 1;

          await customDashboard.fetch(`/divisions/${divisionId}/dashboardTabs/runDenormalizedFindingScript`);
          
          toast.success('National Dashboard Script started successfully!');
        } else {
          toast.success('Action executed successfully!');
        }
        setShowActionModal(false);
        setSelectedFeature(null);
      }
    } catch (error) {
      console.error('Error executing feature action:', error);
      toast.error('Error executing action. Please try again.');
    }
  };

  const ModalForFeatureWithAction = () => {
    return(
      <Modal
        show={showActionModal}
        title="Are you sure you want to run this script?"
        handleClose={() => {
          setShowActionModal(false);
          setSelectedFeature(null);
        }}
        handleSubmit={executeFeatureAction}
        submitBtnText='Run'
        cancelBtn={true}
        cancelBtnText='Close'
      >
        <div style={{ padding: '20px 0' }}>
          <p style={{ 
            fontSize: '16px', 
            lineHeight: '1.5', 
            color: '#333',
            margin: '0'
          }}>
            This may take up to 5-10 minutes to complete and will refresh dashboard data for all tenants. It is recommended to run this during non-peak hours of operation.
          </p>
        </div>
      </Modal>
    )
  }

  return (
    <div>
      {Feature(
        'White Label',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'white_label',
        ),
        handleToggle,
      )}
      {Feature(
        'Precipitation Tracking',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'precipitation_tracking',
        ),
        handleToggle,
      )}
      {Feature(
        'Permission Group',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'permission_group',
        ),
        handleToggle,
      )}
      {Feature(
        'Site Map Editor',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'site_map_editor',
        ),
        handleToggle,
      )}
      {Feature(
        'Reporting',
        featuresList.filter((ele) => ele.feature.feature_type === 'reporting'),
        handleToggle,
      )}
      {MultipleFormsFeature(
        'Project / Findings',
        featuresList.filter(
          (ele) =>
            ele.feature.feature_type === 'projects_findings' &&
            ele.feature.name !== 'Authorization & Agreement Gate',
        ),
        handleToggle,
        userTenantId,
      )}
      {Feature(
        'Inspections',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'authorization_agreement',
        ),
        handleToggle,
      )}
      {Feature(
        'Public View',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'public_view_qr_code',
        ),
        handleToggle,
      )}
      {Feature(
        'System Settings',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'system_settings',
        ),
        handleToggle,
      )}
      {(user.email === 'mmurray@sw2.net' || user.email === 'sqa_superadmin@gmail.com') &&
       FeatureWithAction(
        'National Dashboard Script',
        featuresList.filter(
          (ele) => ele.feature.feature_type === 'national_dashboard_script',
        ),
        handleAction,
      )}
      {Roles()}

      {ModalForFeatureWithAction()}
    </div>
  );
};
